import { AIRPORT_CATALOG_ROUTE, AIRPORT_CATALOG_VERSION } from "./constants";
import {
  buildPlaceIndex,
  matchPlace,
  searchPlaceIndex,
  type AirportRecord,
  type PlaceOption,
  type PlaceResults,
} from "./places";

/** One airport as the catalog route sends it. */
type CatalogRow = [string, string, string, string, string, 0 | 1, string];

type PlaceIndex = ReturnType<typeof buildPlaceIndex>;

const STORAGE_KEY = `cost-estimator:airports:v${AIRPORT_CATALOG_VERSION}`;

const toRecord = ([iata, name, city, country, region, large, keywords]: CatalogRow): AirportRecord => ({
  iata,
  name,
  city,
  country,
  region,
  type: large ? "large_airport" : "medium_airport",
  keywords: keywords ? keywords.split("|") : [],
});

const readStored = (): CatalogRow[] | null => {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as CatalogRow[]) : null;
  } catch {
    return null;
  }
};

const store = (rows: CatalogRow[]) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // Storage full or blocked: the in-memory copy still serves this page.
  }
};

const fetchRows = async (): Promise<CatalogRow[]> => {
  const response = await fetch(`${AIRPORT_CATALOG_ROUTE}?v=${AIRPORT_CATALOG_VERSION}`);
  if (!response.ok) throw new Error(`airport catalog: ${response.status}`);
  const body = (await response.json()) as { airports: CatalogRow[] };
  store(body.airports);
  return body.airports;
};

let loading: Promise<PlaceIndex> | null = null;
let records: AirportRecord[] | null = null;

/**
 * The place index, built once per page from the airport catalog: the session's stored copy, or
 * one request to the catalog route (cached by the browser for a day). Every search and lookup
 * after that runs locally, so typing a city makes no request. A failed load is retried on the
 * next call rather than kept.
 * @returns The index.
 * @throws When the catalog can't be loaded.
 */
export const loadPlaceIndex = (): Promise<PlaceIndex> => {
  if (!loading) {
    const stored = typeof window === "undefined" ? null : readStored();
    loading = (stored ? Promise.resolve(stored) : fetchRows()).then((rows) => {
      records = rows.map(toRecord);
      return buildPlaceIndex(records);
    });
    loading.catch(() => {
      loading = null;
    });
  }
  return loading;
};

/**
 * The airport catalog itself, for picking one airport by code or name (a home city's airport).
 * Loaded the same way as {@link loadPlaceIndex}, so the two share one request.
 * @returns The airports.
 * @throws When the catalog can't be loaded.
 */
export const loadAirportRecords = async (): Promise<AirportRecord[]> => {
  await loadPlaceIndex();
  return records ?? [];
};

/**
 * Real places matching what the planner is typing, ranked by {@link searchPlaceIndex}.
 * @param query - What was typed.
 * @returns The results; empty when the catalog can't be loaded.
 * @throws Never.
 */
export const searchPlaces = async (query: string): Promise<PlaceResults> => {
  try {
    return searchPlaceIndex(query, await loadPlaceIndex());
  } catch {
    return { heading: null, options: [] };
  }
};

/**
 * The places typed names unambiguously mean (pasted lists, attendee files, the assistant's
 * edits), by {@link matchPlace}.
 * @param queries - Names, "City, Country", airport names or codes.
 * @param options - `allowTypos` for the assistant.
 * @returns One result per query, null where nothing matches or the catalog can't be loaded.
 * @throws Never.
 */
export const resolvePlaces = async (
  queries: readonly string[],
  options: { allowTypos?: boolean } = {}
): Promise<Array<PlaceOption | null>> => {
  try {
    const index = await loadPlaceIndex();
    return queries.map((query) => matchPlace(query, index, options));
  } catch {
    return queries.map(() => null);
  }
};
